import {
  CheckBadgeIcon,
  KeyIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, Radio, Spinner, Typography } from "@material-tailwind/react";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { fetchRules } from "redux/features/Admin/rules/rulesSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";

import { z } from "zod";

interface Rules {
  id: number;
  designation: string;
}

const formSchema = z.object({
  handicap: z.union([z.boolean(), z.undefined()]).refine((val) => typeof val === "boolean", {
    message: "Veuillez indiquer si l'espace est accessible aux personnes handicapées.",
  }),
  parking: z.string().min(1, { message: "Veuillez indiquer le type de stationnement." }),
  wc: z.string().min(1, { message: "Veuillez indiquer l'accès aux toilettes." }),
  water: z
    .union([z.boolean(), z.undefined()])
    .refine((val) => typeof val === "boolean", { message: "Veuillez indiquer s'il y a accès à un point d'eau." }),
  light: z
    .union([z.boolean(), z.undefined()])
    .refine((val) => typeof val === "boolean", { message: "Veuillez indiquer s'il y a un éclairage extérieur." }),
  rules: z.array(z.string()).optional(),
  condition: z.array(z.string()).optional(),
});

const LocationAccessibility: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const rulesState = useAppSelector((state) => state.featureRules);

  const [dataLoading, setDataLoading] = useState(true);

  const [tag, setTag] = useState<string>("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      handicap: undefined,
      parking: "",
      wc: "",
      water: undefined,
      light: undefined,
      rules: [],
      condition: [],
    },
    mode: "onChange",
  });

  // Watch des valeurs pour les radio buttons
  const handicapValue = watch("handicap");
  const waterValue = watch("water");
  const lightValue = watch("light");

  useEffect(() => {
    dispatch(fetchRules());
  }, []);

  useEffect(() => {
    if (data) {
      console.log("🔍 LocationAccessibility received data:", {
        handicap: data.handicap,
        water: data.water,
        light: data.light,
        parking: data.parking,
        wc: data.wc,
      });

      // Ne réinitialiser que si les données contiennent des valeurs réelles
      // ET si le formulaire n'a pas été soumis récemment
      const hasRealData =
        data.handicap !== undefined ||
        data.water !== undefined ||
        data.light !== undefined ||
        data.parking ||
        data.wc;

      if (hasRealData && !formSubmitted) {
        console.log("📥 Applying real data to form");
        setValue("handicap", data.handicap ?? undefined);
        setValue("parking", data.parking || "");
        setValue("wc", data.wc || "");
        setValue("water", data.water ?? undefined);
        setValue("light", data.light ?? undefined);
        setValue("rules", data.rules ?? []);
        setValue("condition", data.condition ?? []);
      } else if (formSubmitted) {
        console.log("🚫 Form was submitted recently - ignoring data reset");
      } else {
        console.log("⚠️ Received empty data - keeping current form values");
      }
    }
  }, [data]);

  useEffect(() => {
    if (rulesState.rules) {
      setDataLoading(false);
    }
  }, [rulesState.rules]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFive") {
      // Petit délai pour s'assurer que toutes les valeurs sont synchronisées
      setTimeout(() => {
        console.log("🔥 FormFive submit triggered with current values:", {
          handicap: handicapValue,
          water: waterValue,
          light: lightValue,
          parking: getValues("parking"),
          wc: getValues("wc"),
        });

        // Vérifier si les champs obligatoires sont remplis avant de soumettre
        const currentValues = {
          handicap: handicapValue,
          water: waterValue,
          light: lightValue,
          parking: getValues("parking"),
          wc: getValues("wc"),
        };

        const hasRequiredValues =
          typeof currentValues.handicap === "boolean" &&
          typeof currentValues.water === "boolean" &&
          typeof currentValues.light === "boolean" &&
          currentValues.parking &&
          currentValues.wc;

        if (hasRequiredValues) {
          console.log("✅ All required values present, submitting...");
          handleSubmit(onSubmit)();
        } else {
          console.log("❌ Missing required values, forcing validation...");
          // Forcer la validation pour afficher les erreurs
          trigger();
        }
      }, 100);
    }
  }, [handleForm, handicapValue, waterValue, lightValue]);

  const handleSelect = (ruleDesignation: string, isChecked: boolean) => {
    const updatedselectedRules = isChecked
      ? [...(getValues("rules") ?? []), ruleDesignation]
      : getValues("rules")?.filter((rule: string) => rule !== ruleDesignation);

    setValue("rules", updatedselectedRules as string[]);
    trigger("rules");
  };

  const handleAddTag = () => {
    if (tag.trim() !== "") {
      const dd = getValues("condition") || [];
      setValue("condition", [...dd, tag.trim()]);
      trigger("condition");
      setTag("");
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...(getValues("condition") || [])];
    newTags.splice(index, 1);
    setValue("condition", newTags);
    trigger("condition");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (formData) => {
    console.log("🔥 LocationAccessibility onSubmit - données envoyées:", formData);
    console.log("🔥 Current form values:", {
      handicap: handicapValue,
      water: waterValue,
      light: lightValue,
      parking: getValues("parking"),
      wc: getValues("wc"),
    });
    console.log("🔥 Form errors:", errors);

    // Marquer le formulaire comme soumis pour éviter les réinitialisations
    setFormSubmitted(true);

    // Réinitialiser le flag après un délai pour permettre de futures modifications si nécessaire
    setTimeout(() => {
      setFormSubmitted(false);
    }, 2000);

    FormData(formData);
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              🔧 Accessibilité et commodités
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Informez vos clients des accès et équipements disponibles dans votre espace
            </p>
            <div className="border-b-2 w-20 border-eventToBee mt-4"></div>
          </div>

          {/* Section Accessibilité */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex items-center mb-6">
              <UserCircleIcon className="w-6 h-6 text-eventToBee mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Accessibilité</h3>
            </div>

            <div className="grid grid-cols-1 gap-8">
              {/* Mobilité réduite */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  ♿ Accès aux personnes à mobilité réduite *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      handicapValue === true
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    <Radio
                      color="blue"
                      name="handicap"
                      checked={handicapValue === true}
                      onChange={() => {
                        console.log("🔥 Radio handicap TRUE clicked");
                        setValue("handicap", true);
                        trigger("handicap");
                      }}
                      disabled={isLoading}
                    />
                    <span className="text-gray-700 dark:text-gray-300">Oui, accessible</span>
                  </label>
                  <label
                    className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      handicapValue === false
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    <Radio
                      color="blue"
                      name="handicap"
                      checked={handicapValue === false}
                      onChange={() => {
                        console.log("🔥 Radio handicap FALSE clicked");
                        setValue("handicap", false);
                        trigger("handicap");
                      }}
                      disabled={isLoading}
                    />
                    <span className="text-gray-700 dark:text-gray-300">Non accessible</span>
                  </label>
                </div>
                {errors.handicap?.message && (
                  <div className="text-xs text-red-500 mt-2 flex items-center space-x-1">
                    <span>⚠️</span>
                    <span>{errors.handicap.message}</span>
                  </div>
                )}
              </div>

              {/* Stationnement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  🚗 Accès au stationnement *
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {["libre", "privé", "extérieur"].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        getValues("parking") === option
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                    >
                      <Radio
                        color="blue"
                        name="parking"
                        checked={getValues("parking") === option}
                        onChange={() => {
                          setValue("parking", option);
                          trigger("parking");
                        }}
                        disabled={isLoading}
                      />
                      <span className="text-gray-700 dark:text-gray-300 capitalize">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.parking?.message && (
                  <div className="text-xs text-red-500 mt-2 flex items-center space-x-1">
                    <span>⚠️</span>
                    <span>{errors.parking.message}</span>
                  </div>
                )}
              </div>

              {/* Toilettes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  🚽 Accès aux toilettes *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {["intérieur", "extérieur"].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        getValues("wc") === option
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                    >
                      <Radio
                        color="blue"
                        name="wc"
                        checked={getValues("wc") === option}
                        onChange={() => {
                          setValue("wc", option);
                          trigger("wc");
                        }}
                        disabled={isLoading}
                      />
                      <span className="text-gray-700 dark:text-gray-300 capitalize">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.wc?.message && (
                  <div className="text-xs text-red-500 mt-2 flex items-center space-x-1">
                    <span>⚠️</span>
                    <span>{errors.wc.message}</span>
                  </div>
                )}
              </div>

              {/* Point d'eau et Éclairage */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Accès au point d'eau *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[true, false].map((value) => (
                      <label
                        key={value.toString()}
                        className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          waterValue === value
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-200 dark:border-gray-600"
                        }`}
                      >
                        <Radio
                          color="blue"
                          name="water"
                          checked={waterValue === value}
                          onChange={() => {
                            setValue("water", value);
                            trigger("water");
                          }}
                          disabled={isLoading}
                        />
                        <span className="text-gray-700 dark:text-gray-300">{value ? "Oui" : "Non"}</span>
                      </label>
                    ))}
                  </div>
                  {errors.water?.message && (
                    <div className="text-xs text-red-500 mt-1">{errors.water.message}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Éclairage extérieur *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[true, false].map((value) => (
                      <label
                        key={value.toString()}
                        className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          lightValue === value
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-200 dark:border-gray-600"
                        }`}
                      >
                        <Radio
                          color="blue"
                          name="light"
                          checked={lightValue === value}
                          onChange={() => {
                            setValue("light", value);
                            trigger("light");
                          }}
                          disabled={isLoading}
                        />
                        <span className="text-gray-700 dark:text-gray-300">{value ? "Oui" : "Non"}</span>
                      </label>
                    ))}
                  </div>
                  {errors.light?.message && (
                    <div className="text-xs text-red-500 mt-1">{errors.light.message}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section Règles */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <ShieldCheckIcon className="w-6 h-6 text-eventToBee mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Règles et conditions d'utilisation
              </h3>
            </div>

            {/* Règles prédéfinies */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Règles communes (optionnel)
              </label>
              {dataLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner color="blue" className="w-8 h-8" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {rulesState.rules?.map((rule: Rules) => (
                    <label
                      key={rule.designation}
                      className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <Checkbox
                        checked={!!getValues("rules")?.includes(rule.designation)}
                        onChange={(e) => handleSelect(rule.designation, e.target.checked)}
                        className="text-eventToBee"
                      />
                      <Typography className="text-sm text-gray-700 dark:text-gray-300">
                        {rule.designation}
                      </Typography>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Règles personnalisées */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Règles supplémentaires (optionnel)
              </label>

              {/* Règles ajoutées */}
              {getValues("condition") && getValues("condition")!.length > 0 && (
                <div className="mb-4 space-y-2">
                  {getValues("condition")?.map((tag: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <i className="las la-times text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Ajout de nouvelles règles */}
              <div className="flex gap-3">
                <Input
                  className="flex-1"
                  placeholder="Ex: Interdiction de fumer, Pas d'animaux..."
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <ButtonPrimary onClick={handleAddTag} type="button" className="px-6">
                  Ajouter
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAccessibility;
